import {Component, ElementRef, Input, OnChanges, SimpleChanges, ChangeDetectionStrategy} from '@angular/core';
import {VisitContext} from "../../visit-details/visit-context";
import {PortvisitUtils} from "../../refdata/portvisit-utils";
import {
  DeclareTransit,
  MergeTransitTemplate,
  SaveTransit,
  TransitTemplate,
  TryCancelTransit
} from '@portbase/bezoekschip-service-typescriptmodels';
import {checkValidity, cloneObject, lodash, sendCommand} from '../../common/utils';
import {AppContext} from '../../app-context';
import {
  addEmptyGoodsItem,
  addEmptyGoodsItemAndGuaranteeIfNecessary,
  fixUpTransitDataBeforeDeclaration,
  fixUpTransitDataBeforeSaving,
  formatSendDate,
  initializeTransitData,
  loadTransit,
  Transit,
  undoIncompatibleTransitDataFixUps,
  updateSummary
} from "../transit.utils";
import moment from "moment";
import {EventGateway} from '../../common/event-gateway';

@Component({
    selector: 'app-transit-declaration',
    templateUrl: './transit-declaration.component.html',
    styleUrls: ['./transit-declaration.component.css'],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class TransitDeclarationComponent implements OnChanges {
  context = VisitContext;
  appContext = AppContext;
  refData = PortvisitUtils;

  @Input() transit: Transit;

  constructor(private element: ElementRef, private eventGateway: EventGateway) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    loadTransit(this.transit);
  }

  findTransitTemplates = term => !this.transit.declaration ? null :
    this.refData.findTransitTemplates(term, this.transit.declaration.declarantShortName);

  addEmptyGoodsItem() {
    addEmptyGoodsItem(this.transit.declaration.data);
  };

  getAnyRejectReason() {
    const status = this.transit.declaration?.status;

    if (!status) {
      return null;
    } else if (status.cancellation && status.cancellation.reason) {
      return status.cancellation.reason;
    } else if (status.releaseRejected && status.releaseRejected.reason) {
      return status.releaseRejected.reason;
    } else if (status.reject && status.reject.reason) {
      return status.reject.reason;
    } else if (status.insufficientGuarantee && status.insufficientGuarantee.reason) {
      return status.insufficientGuarantee.reason;
    } else {
      return null;
    }
  }

  save() {
    sendCommand('com.portbase.bezoekschip.common.api.transit.SaveTransit', <SaveTransit>{
      declarantShortName: this.transit.declaration.declarantShortName,
      data: fixUpTransitDataBeforeSaving(this.transit.declaration.data),
      lrn: this.transit.declaration.lrn,
      crn: this.transit.declaration.crn,
      sendOnAta: !!this.transit.declaration.status.sendOnAta,
      sendDate: formatSendDate(this.transit.declaration.status.sendDate, this.transit.declaration.status.sendOnAta)
    }, () => {
      updateSummary(this.transit.summary, this.transit.declaration.data, false);
      this.transit.isContainerDeclaration = !!this.transit.declaration.data.containerNumber;
      this.transit.isNewTransitDeclaration = false;
      AppContext.registerSuccess('The declaration was saved successfully.');
    });
  }

  send() {
    if (checkValidity(this.element)) {
      const transitData = fixUpTransitDataBeforeDeclaration(this.transit.declaration.data);

      if (transitData !== null) {
        sendCommand('com.portbase.bezoekschip.common.api.transit.DeclareTransit', <DeclareTransit>{
          declarantShortName: this.transit.declaration.declarantShortName,
          data: transitData,
          lrn: this.transit.declaration.lrn,
          crn: this.transit.declaration.crn,
          sendOnAta: !!this.transit.declaration.status.sendOnAta,
          sendDate: formatSendDate(this.transit.declaration.status.sendDate, this.transit.declaration.status.sendOnAta)
        }, () => {
          updateSummary(this.transit.summary, transitData, true);
          this.transit.isContainerDeclaration = !!transitData.containerNumber;
          this.transit.isNewTransitDeclaration = false;
          AppContext.registerSuccess('The declaration was sent successfully.');
        });

        undoIncompatibleTransitDataFixUps(transitData);
      } else {
        undoIncompatibleTransitDataFixUps(this.transit.declaration.data);
      }
    }
  }

  cancel(reason: string, form: HTMLElement) {
    if (checkValidity(form)) {
      sendCommand('com.portbase.bezoekschip.common.api.transit.TryCancelTransit',
        <TryCancelTransit>{crn: this.transit.declaration.crn, lrn: this.transit.declaration.lrn, reason: reason},
        () => AppContext.registerSuccess('The declaration was cancelled successfully.'));
      const modal = $('#cancelTransit');
      modal.modal('hide')
    }
  }

  saveTemplate() {
    if (this.transit.declaration.data.templateName) {
      const clonedTransitData = cloneObject(this.transit.declaration.data);

      clonedTransitData.containerNumber = null;
      clonedTransitData.goodsItems = null;
      clonedTransitData.houseConsignments = null;

      sendCommand('com.portbase.bezoekschip.common.api.refdata.MergeTransitTemplate', <MergeTransitTemplate>{
        id: this.transit.declaration.data.templateName,
        value: <TransitTemplate>{
          id: this.transit.declaration.data.templateName,
          name: this.transit.declaration.data.templateName,
          templateData: clonedTransitData,
          ownerShortName: AppContext.userProfile.organisation?.shortName
        },
        delete: false
      }, () => {
        AppContext.registerSuccess('The transit template was saved successfully.');
      })
    } else {
      AppContext.registerError("Please fill out the transit template name.")
    }
  }

  useTemplate = ($event: TransitTemplate) => {
    this.transit.declaration.data = initializeTransitData(lodash.cloneDeep($event.templateData));
    this.transit.declaration.requestedTransportExpiryDate = moment().add(this.transit.declaration.data.transportTerm ?
      this.transit.declaration.data.transportTerm : 0, 'seconds').toISOString();
    addEmptyGoodsItemAndGuaranteeIfNecessary(this.transit.declaration.data);
  }

  isCancellable = (): boolean => {
    const status = this.transit.declaration?.status;

    if (status && (!status.cancellation && !status.cancellationRequest && !status.release) &&
      (status.releaseRejected || status.reject || status.insufficientGuarantee || status.accept
        || (!status.sent && status.declarationDate))) {
      return this.appContext.isTransitEditor();
    } else {
      return false;
    }
  };

  isEditable = (): boolean => {
    const status = this.transit.declaration?.status;

    if (status && (!status.cancellation && !status.cancellationRequest && !status.release) &&
      (!status.sent || (!status.accept && status.reject))) {
      return this.appContext.isTransitEditor();
    } else {
      return false;
    }
  };

  createNext() {
    this.eventGateway.publish("createNextTransitDeclaration", this.transit);
  }

  copy() {
    this.eventGateway.publish("copyTransitDeclaration", this.transit);
  }
}
